import { memo, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { ComboTheme } from './types';

/** A category's place on the chart, which hovering or tapping picks. */
export interface CategoryBand {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CategoryTooltipRow {
  color: string;
  name?: string;
  value: string;
}

const kMaxWidth = 240;

/**
 * Over a category chart: hovering a category (or tapping it, on touch)
 * shows its full name — the axis may have cut it — and each series' value.
 */
export const CategoryTooltip = memo(
  ({
    width,
    height,
    bands,
    names,
    rowsOf,
    theme,
  }: {
    width: number;
    height: number;
    bands: CategoryBand[];
    /** In full, one per band. */
    names: string[];
    rowsOf: (index: number) => CategoryTooltipRow[];
    theme: ComboTheme;
  }) => {
    const [hovered, setHovered] = useState<number | null>(null);
    const [pinned, setPinned] = useState<number | null>(null);
    // Measured for one category: a box not yet measured for the one shown
    // stays invisible for a frame rather than jump.
    const [size, setSize] = useState({ index: -1, width: 0, height: 0 });
    const active = hovered ?? pinned;
    const band = active === null ? null : bands[active];
    const f = theme.fontSize;
    let left = 0;
    let top = 0;
    // Beside a column, under (or over) a row: never over the category.
    if (band && band.height > band.width) {
      const after = band.x + band.width + 4;
      left =
        after + size.width <= width
          ? after
          : Math.max(band.x - size.width - 4, 2);
      top = band.y + 4;
    } else if (band) {
      const center = band.x + band.width / 2;
      left = Math.min(
        Math.max(center - size.width / 2, 2),
        Math.max(width - size.width - 2, 2),
      );
      const below = band.y + band.height + 4;
      const above = band.y - size.height - 4;
      top =
        below + size.height <= height ? below : above >= 0 ? above : band.y + 4;
    }
    return (
      <View
        pointerEvents="box-none"
        style={{ position: 'absolute', left: 0, top: 0, width, height }}
      >
        {bands.map((entry, i) => (
          <Pressable
            key={i}
            accessibilityLabel={[
              names[i],
              ...rowsOf(i).map(row =>
                row.name ? `${row.name} ${row.value}` : row.value,
              ),
            ].join('，')}
            onHoverIn={() => setHovered(i)}
            onHoverOut={() => setHovered(now => (now === i ? null : now))}
            // A pointer that hovers already shows it; a tap pins it.
            onPress={() => {
              if (hovered === null) setPinned(now => (now === i ? null : i));
            }}
            style={{
              position: 'absolute',
              left: entry.x,
              top: entry.y,
              width: entry.width,
              height: entry.height,
              backgroundColor: active === i ? theme.grid : 'transparent',
              opacity: active === i ? 0.4 : 1,
            }}
          />
        ))}
        {band && active !== null ? (
          <View
            // A box per category: on web onLayout reports size changes
            // only, so a box reused for a same-sized category would never
            // be measured for it and stay invisible.
            key={active}
            pointerEvents="none"
            onLayout={event => {
              const { width: w, height: h } = event.nativeEvent.layout;
              if (
                size.index !== active ||
                w !== size.width ||
                h !== size.height
              )
                setSize({ index: active, width: w, height: h });
            }}
            style={{
              position: 'absolute',
              left,
              top,
              opacity: size.index === active ? 1 : 0,
              maxWidth: Math.min(kMaxWidth, width - 4),
              paddingHorizontal: 8,
              paddingVertical: 6,
              gap: 3,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: theme.axisLine,
              backgroundColor: theme.background,
              shadowColor: '#0F172A',
              shadowOpacity: 0.08,
              shadowRadius: 6,
              shadowOffset: { width: 0, height: 2 },
              elevation: 2,
            }}
          >
            <Text
              style={{
                fontSize: f + 1,
                fontFamily: theme.fontFamily,
                color: theme.label,
              }}
            >
              {names[active]}
            </Text>
            {rowsOf(active).map((row, i) => (
              <View
                key={i}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
              >
                <View
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 4,
                    backgroundColor: row.color,
                  }}
                />
                <Text
                  style={{
                    flexShrink: 1,
                    fontSize: f,
                    fontFamily: theme.fontFamily,
                    color: theme.axisTitle,
                  }}
                >
                  {row.name ? `${row.name}  ${row.value}` : row.value}
                </Text>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    );
  },
);
